/**
 * Catálogo de indicadores: onde cada um mora e de que dado ele depende.
 *
 * DUAS COISAS QUE ESTE ARQUIVO RESOLVE
 *
 * 1. **A fronteira dos blocos é a dependência de DADO, não o tema.** Antes,
 *    "Dívida" era um bloco só, com metade dos indicadores exigindo balanço e
 *    metade não — e o bloco aparecia meio preenchido, que é exatamente o que a
 *    degradação por bloco inteiro existe para impedir. Agora `exigeBalanco` é
 *    propriedade de cada INDICADOR e a da seção é derivada dele.
 *
 * 2. **O CTA de destravar precisa dizer os nomes.** "Informe o balanço" não
 *    convence ninguém; "5 campos destravam Liquidez Corrente, Liquidez Seca,
 *    Ciclo Financeiro, NCG e mais N" convence. A lista sai daqui, gerada do
 *    catálogo — nunca escrita à mão, porque lista escrita à mão desatualiza.
 */

export const TELAS = {
  DADOS: 'dados',
  SAUDE: 'saude',
  RESULTADO: 'resultado',
  CAIXA_CICLO: 'caixa-ciclo',
  DIVIDA_RETORNO: 'divida-retorno',
  PAINEL: 'painel',
  ANALISE: 'analise'
};

/**
 * As seções que degradam como uma unidade. Cada uma fecha inteira ou abre
 * inteira — nunca um card cinza no meio de outros preenchidos.
 */
export const SECOES = {
  RESULTADO: 'resultado',
  ESTRUTURA_CUSTOS: 'estrutura-custos',
  LIQUIDEZ: 'liquidez',
  CICLO: 'ciclo',
  CUSTO_DIVIDA: 'custo-divida',
  ESTRUTURA_CAPITAL: 'estrutura-capital'
};

/**
 * Os 5 campos que o produto promete: são os que abrem liquidez e ciclo.
 * O balanço completo (10 campos) abre também endividamento e retorno.
 */
export const CAMPOS_DO_BALANCO_CURTO = [
  'Saldo em caixa e bancos',
  'Contas a receber',
  'Estoque',
  'Fornecedores a pagar',
  'Empréstimos de curto prazo'
];

export const CATALOGO_DE_INDICADORES = [
  // --- Resultado (funciona 100% sem balanço) ---
  { chave: 'receitaLiquida', nome: 'Receita Líquida', tela: TELAS.RESULTADO, secao: SECOES.RESULTADO, exigeBalanco: false, formato: 'moeda' },
  { chave: 'margemBruta', nome: 'Margem Bruta', tela: TELAS.RESULTADO, secao: SECOES.RESULTADO, exigeBalanco: false, formato: 'percentual' },
  { chave: 'margemContribuicao', nome: 'Margem de Contribuição', tela: TELAS.RESULTADO, secao: SECOES.RESULTADO, exigeBalanco: false, formato: 'percentual', faixa: 'margemContribuicao' },
  { chave: 'margemLiquida', nome: 'Margem Líquida', tela: TELAS.RESULTADO, secao: SECOES.RESULTADO, exigeBalanco: false, formato: 'percentual', faixa: 'margemLiquida' },
  { chave: 'margemLiquidaCompetencia', nome: 'Margem Líquida com CMV', tela: TELAS.RESULTADO, secao: SECOES.RESULTADO, exigeBalanco: false, formato: 'percentual', faixa: 'margemLiquida' },
  { chave: 'ebitdaProxy', nome: 'Resultado Operacional (LAJIR aprox.)', tela: TELAS.RESULTADO, secao: SECOES.RESULTADO, exigeBalanco: false, formato: 'moeda' },
  { chave: 'pontoEquilibrio', nome: 'Ponto de Equilíbrio', tela: TELAS.RESULTADO, secao: SECOES.RESULTADO, exigeBalanco: false, formato: 'moeda' },
  { chave: 'margemSeguranca', nome: 'Margem de Segurança', tela: TELAS.RESULTADO, secao: SECOES.RESULTADO, exigeBalanco: false, formato: 'percentual', faixa: 'margemSeguranca' },

  // --- Estrutura de custos (para onde vai cada real) ---
  { chave: 'custosFixosSobreReceita', nome: 'Custos Fixos / Receita Líquida', tela: TELAS.RESULTADO, secao: SECOES.ESTRUTURA_CUSTOS, exigeBalanco: false, formato: 'percentual', faixa: 'custosFixosSobreReceita' },
  { chave: 'pessoalSobreReceita', nome: 'Folha e encargos / Receita Líquida', tela: TELAS.RESULTADO, secao: SECOES.ESTRUTURA_CUSTOS, exigeBalanco: false, formato: 'percentual', faixa: 'pessoalSobreReceita' },
  { chave: 'marketingSobreReceita', nome: 'Marketing / Receita Líquida', tela: TELAS.RESULTADO, secao: SECOES.ESTRUTURA_CUSTOS, exigeBalanco: false, formato: 'percentual' },
  { chave: 'ccmSobreReceita', nome: 'Taxas de meio de pagamento / Receita Líquida', tela: TELAS.RESULTADO, secao: SECOES.ESTRUTURA_CUSTOS, exigeBalanco: false, formato: 'percentual' },
  { chave: 'fornecedorTop1Share', nome: 'Dependência do maior fornecedor', tela: TELAS.RESULTADO, secao: SECOES.ESTRUTURA_CUSTOS, exigeBalanco: false, formato: 'percentual', faixa: 'fornecedorTop1Share' },

  // --- Caixa (exige balanço) ---
  { chave: 'liquidezCorrente', nome: 'Liquidez Corrente', tela: TELAS.CAIXA_CICLO, secao: SECOES.LIQUIDEZ, exigeBalanco: true, formato: 'ratio', faixa: 'liquidezCorrente' },
  { chave: 'liquidezSeca', nome: 'Liquidez Seca', tela: TELAS.CAIXA_CICLO, secao: SECOES.LIQUIDEZ, exigeBalanco: true, formato: 'ratio', faixa: 'liquidezSeca' },
  { chave: 'liquidezImediata', nome: 'Liquidez Imediata', tela: TELAS.CAIXA_CICLO, secao: SECOES.LIQUIDEZ, exigeBalanco: true, formato: 'ratio', faixa: 'liquidezImediata' },
  { chave: 'capitalDeGiro', nome: 'Capital de Giro (CDG)', tela: TELAS.CAIXA_CICLO, secao: SECOES.LIQUIDEZ, exigeBalanco: true, formato: 'moeda' },
  { chave: 'cashToDebt', nome: 'Caixa sobre dívida de curto prazo', tela: TELAS.CAIXA_CICLO, secao: SECOES.LIQUIDEZ, exigeBalanco: true, formato: 'decimal' },
  { chave: 'runwayMeses', nome: 'Runway de caixa', tela: TELAS.CAIXA_CICLO, secao: SECOES.LIQUIDEZ, exigeBalanco: true, formato: 'decimal', faixa: 'runwayMeses' },

  // --- Ciclo (exige balanço) ---
  { chave: 'dso', nome: 'PMRV — Prazo Médio de Recebimento', tela: TELAS.CAIXA_CICLO, secao: SECOES.CICLO, exigeBalanco: true, formato: 'dias' },
  { chave: 'dio', nome: 'PMRE — Prazo Médio de Estoque', tela: TELAS.CAIXA_CICLO, secao: SECOES.CICLO, exigeBalanco: true, formato: 'dias' },
  { chave: 'dpo', nome: 'PMPC — Prazo Médio de Pagamento', tela: TELAS.CAIXA_CICLO, secao: SECOES.CICLO, exigeBalanco: true, formato: 'dias' },
  { chave: 'cicloOperacional', nome: 'Ciclo Operacional', tela: TELAS.CAIXA_CICLO, secao: SECOES.CICLO, exigeBalanco: true, formato: 'dias' },
  { chave: 'ccc', nome: 'Ciclo Financeiro', tela: TELAS.CAIXA_CICLO, secao: SECOES.CICLO, exigeBalanco: true, formato: 'dias', faixa: 'cicloFinanceiro' },
  { chave: 'ncg', nome: 'Necessidade de Capital de Giro (NCG)', tela: TELAS.CAIXA_CICLO, secao: SECOES.CICLO, exigeBalanco: true, formato: 'moeda' },
  { chave: 'saldoTesouraria', nome: 'Saldo de Tesouraria', tela: TELAS.CAIXA_CICLO, secao: SECOES.CICLO, exigeBalanco: true, formato: 'moeda' },

  // --- Custo da dívida (o que o DRE já responde sozinho) ---
  { chave: 'despesasFinanceiras', nome: 'Juros e tarifas', tela: TELAS.DIVIDA_RETORNO, secao: SECOES.CUSTO_DIVIDA, exigeBalanco: false, formato: 'moeda' },
  { chave: 'jurosSobreReceita', nome: 'Juros / Receita Líquida', tela: TELAS.DIVIDA_RETORNO, secao: SECOES.CUSTO_DIVIDA, exigeBalanco: false, formato: 'percentual' },
  { chave: 'icr', nome: 'ICR — Cobertura de Juros', tela: TELAS.DIVIDA_RETORNO, secao: SECOES.CUSTO_DIVIDA, exigeBalanco: false, formato: 'decimal', faixa: 'icr' },
  { chave: 'dscr', nome: 'DSCR — Cobertura do Serviço da Dívida', tela: TELAS.DIVIDA_RETORNO, secao: SECOES.CUSTO_DIVIDA, exigeBalanco: false, formato: 'decimal', faixa: 'dscr' },

  // --- Estrutura de capital e retorno (exige balanço) ---
  { chave: 'endividamento', nome: 'Índice de Endividamento', tela: TELAS.DIVIDA_RETORNO, secao: SECOES.ESTRUTURA_CAPITAL, exigeBalanco: true, formato: 'percentual', faixa: 'endividamento' },
  { chave: 'composicaoEndividamento', nome: 'Composição do Endividamento (curto prazo)', tela: TELAS.DIVIDA_RETORNO, secao: SECOES.ESTRUTURA_CAPITAL, exigeBalanco: true, formato: 'percentual', faixa: 'composicaoEndividamento' },
  // O motor calcula Passivo / PL, que é dívida sobre capital próprio — NÃO é o
  // multiplicador do DuPont, que é Ativo / PL. Dois números diferentes com o
  // mesmo nome na mesma tela é como este produto acabou com quatro pares assim.
  // O NOME MENTIA SOBRE A CONTA. Ele dizia "Dívida sobre patrimônio", mas o
  // divisor é o passivo TOTAL, que neste modelo de dados é Passivo + PL,
  // ou seja, o Ativo — a conta é o multiplicador de alavancagem. Na tela de
  // Balanço isso ficou impossível de ignorar: "Dívida sobre patrimônio 1,94"
  // logo abaixo de "Dívida total: R$ 155.000" e "Capital dos sócios:
  // R$ 165.000", que dão 0,94. Quem conferisse chegaria a outro número. A
  // fórmula está certa e reconcilia com a planilha; o rótulo é que estava
  // errado, e renomear não mexe em nenhum valor calculado.
  { chave: 'alavancagemFinanceira', nome: 'Multiplicador de alavancagem (Ativo ÷ PL)', tela: TELAS.DIVIDA_RETORNO, secao: SECOES.ESTRUTURA_CAPITAL, exigeBalanco: true, formato: 'decimal' },
  { chave: 'giroDoAtivo', nome: 'Giro do Ativo', tela: TELAS.DIVIDA_RETORNO, secao: SECOES.ESTRUTURA_CAPITAL, exigeBalanco: true, formato: 'decimal' },
  { chave: 'roe', nome: 'ROE — Retorno sobre o Patrimônio', tela: TELAS.DIVIDA_RETORNO, secao: SECOES.ESTRUTURA_CAPITAL, exigeBalanco: true, formato: 'percentual' },
  { chave: 'roa', nome: 'ROA — Retorno sobre o Ativo', tela: TELAS.DIVIDA_RETORNO, secao: SECOES.ESTRUTURA_CAPITAL, exigeBalanco: true, formato: 'percentual' }
];

/** Índice por chave, para quem só tem o nome do indicador na mão. */
export const porChave = CATALOGO_DE_INDICADORES.reduce((indice, item) => {
  indice[item.chave] = item;
  return indice;
}, {});

/** `true` quando TODO indicador da seção depende do balanço. */
export const secaoExigeBalanco = (secao) => {
  const itens = CATALOGO_DE_INDICADORES.filter((i) => i.secao === secao);
  return itens.length > 0 && itens.every((i) => i.exigeBalanco);
};

/** Os nomes que o balanço destrava — o texto do CTA sai daqui, não da mão. */
export const indicadoresBloqueadosSemBalanco = (secao = null) =>
  CATALOGO_DE_INDICADORES
    .filter((i) => i.exigeBalanco && (secao === null || i.secao === secao))
    .map((i) => i.nome);

/**
 * Chaves usadas pelas 39 regras (`insightsService.buildFeatures`) mapeadas para
 * a tela que explica aquele número. É o que faz um sinal virar link em vez de
 * texto solto: o usuário lê "ciclo financeiro em 87 dias" e chega no bloco que
 * mostra por quê.
 *
 * O sufixo `_m3` sobreviveu ao rename e continua saindo das regras salvas por
 * cliente — a normalização mora aqui, num lugar só.
 */
const TELA_POR_PREFIXO = [
  ['faturamento', TELAS.RESULTADO],
  ['crescimento', TELAS.RESULTADO],
  ['margem_liquida', TELAS.RESULTADO],
  ['margem_contribuicao', TELAS.RESULTADO],
  ['margem_bruta', TELAS.RESULTADO],
  ['margem_seguranca', TELAS.RESULTADO],
  ['ponto_equilibrio', TELAS.RESULTADO],
  ['ebitda', TELAS.RESULTADO],
  ['custos_fixos', TELAS.RESULTADO],
  ['custos_variaveis', TELAS.RESULTADO],
  ['pessoal', TELAS.RESULTADO],
  ['marketing', TELAS.RESULTADO],
  ['ccm', TELAS.RESULTADO],
  ['fornecedor', TELAS.RESULTADO],
  ['liquidez', TELAS.CAIXA_CICLO],
  ['capital_giro', TELAS.CAIXA_CICLO],
  ['runway', TELAS.CAIXA_CICLO],
  ['cash_to_debt', TELAS.CAIXA_CICLO],
  ['ncg', TELAS.CAIXA_CICLO],
  ['tesouraria', TELAS.CAIXA_CICLO],
  ['ciclo', TELAS.CAIXA_CICLO],
  ['dso', TELAS.CAIXA_CICLO],
  ['dio', TELAS.CAIXA_CICLO],
  ['dpo', TELAS.CAIXA_CICLO],
  ['ccc', TELAS.CAIXA_CICLO],
  ['icr', TELAS.DIVIDA_RETORNO],
  ['dscr', TELAS.DIVIDA_RETORNO],
  ['divida', TELAS.DIVIDA_RETORNO],
  ['endividamento', TELAS.DIVIDA_RETORNO],
  ['alavancagem', TELAS.DIVIDA_RETORNO],
  ['despesas_financeiras', TELAS.DIVIDA_RETORNO],
  ['roe', TELAS.DIVIDA_RETORNO],
  ['roa', TELAS.DIVIDA_RETORNO]
];

/**
 * O nome legível de um indicador citado por uma regra.
 *
 * As regras falam em `liquidez_corrente` e `ccm_sobre_receita_m3` — chaves de
 * máquina, e elas vazavam cruas para a tela dentro do plano de ação: o cartão
 * exibia "KPIs: ccm_sobre_receita_m3" para um empresário de padaria. O catálogo
 * já guarda o nome que o produto usa em toda outra tela; isto aqui é só a ponte
 * entre as duas grafias.
 *
 * Sem palpite inventado: chave que o catálogo não conhece volta com os
 * sublinhados abertos, que ainda se lê, em vez de sumir e levar junto a única
 * indicação de o que acompanhar.
 */
/**
 * Grandezas que as regras citam e que o catálogo não lista.
 *
 * O catálogo é o que a tela Painel de Indicadores RENDERIZA, e faturamento e
 * crescimento não são linhas dele — aparecem como protagonistas nas telas de
 * Resultado e Saúde. Só que as regras os citam como KPI, e sem nome aqui o id
 * da regra vazava cru para o chip: o Plano de Ação exibia "Acompanhar:
 * crescimento", em minúscula, ao lado de "Acompanhar: Margem Líquida". Nome de
 * indicador com aparência de variável de código, num documento de consultoria
 * de R$ 497 por mês, é a trinca que faz o leitor duvidar do resto.
 *
 * Apelido, e não entrada nova no catálogo: entrada nova acrescentaria linha na
 * tela do Painel, e o problema é de rótulo, não de indicador faltando.
 */
const APELIDOS = {
  faturamento: 'Faturamento',
  crescimento: 'Crescimento do faturamento'
};

export const nomeDoIndicador = (chaveDaRegra) => {
  if (!chaveDaRegra) return '';
  const bruta = String(chaveDaRegra).trim();
  // O sufixo `_m3` diz "no mês 3 da janela"; é recorte, e não parte do nome.
  const semJanela = bruta.replace(/_m\d+$/, '');
  const camel = semJanela.replace(/_([a-z])/g, (_, letra) => letra.toUpperCase());
  return porChave[camel]?.nome || APELIDOS[camel] || semJanela.replace(/_/g, ' ');
};

/** A tela que explica um indicador citado por uma regra. `null` sem palpite. */
export const telaDoIndicador = (chaveDaRegra) => {
  if (!chaveDaRegra) return null;
  const normalizada = String(chaveDaRegra).toLowerCase();
  const achado = TELA_POR_PREFIXO.find(([prefixo]) => normalizada.startsWith(prefixo));
  return achado ? achado[1] : null;
};
